// Context + hooks colocated by design — split would be more files than warranted.
/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

interface PageHeaderState {
  actions: ReactNode | null;
  setActions: (a: ReactNode | null) => void;
}

const Ctx = createContext<PageHeaderState | undefined>(undefined);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [actions, setActions] = useState<ReactNode | null>(null);
  return <Ctx.Provider value={{ actions, setActions }}>{children}</Ctx.Provider>;
}

/**
 * Register page-specific action buttons that render in the Topbar action slot.
 * Pass `null` (or just don't call) to clear. Cleans up automatically on unmount.
 *
 * No-ops outside a `PageHeaderProvider` so pages stay renderable in isolation
 * (integration tests, Storybook). Production pages always render inside
 * MainLayout which wraps in a provider.
 */
export function usePageHeaderActions(actions: ReactNode | null): void {
  const ctx = useContext(Ctx);
  const setActions = ctx?.setActions;
  useEffect(() => {
    if (!setActions) return;
    setActions(actions);
    return () => setActions(null);
  }, [actions, setActions]);
}

/** Used by MainLayout's Topbar to read the current page's action slot. */
export function usePageHeaderActionsSlot(): ReactNode | null {
  const ctx = useContext(Ctx);
  return ctx?.actions ?? null;
}
